import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {Content} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {getButtonsData} from 'src/common/helpers'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import type {Context} from 'src/common/types'

export type GradientFinalCTAProps = SliceComponentProps<Content.GradientFinalCtaSlice>

const GradientFinalCTA = ({
  slice,
  context: {pageUid, buttonsData},
}: GradientFinalCTAProps & {context: Context}): JSX.Element => {
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()

  const {primaryButtonData: buttonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.book_an_intro_button,
  })

  const buttonHref = buttonData?.link ? linkResolver(buttonData.link, 'comparison-cta') : null
  const buttonLabel = buttonData?.label

  const trackButtonClick = () => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: buttonData?.marketing_id || 'comparison-final-cta',
        elementText: buttonLabel || '',
        location: 'comparison-final-cta',
        type: 'click',
      },
      page: {domainCountry, type: pageUid || 'unknown'},
    })
  }

  const p = slice.primary
  const isDark = p.background_color === 'Dark green'

  const bgMap: Record<string, string> = {
    'Dark green': 'bg-green-dark',
    'Grey/100': 'bg-moss-grey-100',
    'Moss White': 'bg-moss-white',
  }

  const bgClass = bgMap[p.background_color ?? 'Dark green'] ?? 'bg-green-dark'
  const ptClass = PADDING_TOP_CLASS[p.padding_top ?? DEFAULT_PADDING] ?? ''
  const pbClass = PADDING_BOTTOM_CLASS[p.padding_bottom ?? DEFAULT_PADDING] ?? ''

  const gradientBg = isDark
    ? 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(180,255,68,0.10), transparent 70%)'
    : 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(180,255,68,0.15), transparent 70%)'

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn('relative overflow-hidden text-center', bgClass, ptClass, pbClass)}
    >
      <div className="pointer-events-none absolute inset-0" style={{background: gradientBg}} />
      <div className="relative mx-auto max-w-[1280px] px-10">
        {p.eyebrow && (
          <span
            className={cn(
              'mb-6 block text-[11px] font-semibold uppercase tracking-widest',
              isDark ? 'text-green-neon' : 'text-green-deep',
            )}
          >
            {p.eyebrow}
          </span>
        )}
        <h2
          className={cn(
            'mb-8 font-light leading-[0.9] tracking-[-0.02em]',
            isDark ? 'text-moss-white' : 'text-moss-black',
          )}
          style={{fontSize: 'clamp(56px, 8vw, 120px)'}}
        >
          {p.heading}
          {p.heading_accent && (
            <>
              <br />
              <span className="text-green-neon">{p.heading_accent}</span>
            </>
          )}
        </h2>
        {p.subtext && (
          <p
            className={cn(
              'mx-auto mb-10 max-w-[560px] text-[20px] leading-relaxed',
              isDark ? 'text-moss-white/70' : 'text-black/65',
            )}
          >
            {p.subtext}
          </p>
        )}
        {buttonHref && buttonLabel && (
          <Link
            href={buttonHref}
            className="text-button-s inline-flex h-12 items-center rounded-[4px] bg-green-neon px-4 py-3 uppercase text-black hover:bg-neon-550"
            onClick={trackButtonClick}
          >
            {buttonLabel}
          </Link>
        )}
      </div>
    </section>
  )
}

export default GradientFinalCTA
